/**
 * Render-storm tripwire (OOM open incident, Miles 2026-07-12).
 *
 * /desktop crashed with Chrome's "Aw, Snap! Out of Memory" — the confirmed
 * layout-feedback loop (overflow indicators as flex siblings) was fixed in
 * #500, but "keeps crashing" reports arrived after the merge, so the root
 * cause is treated as UNPROVEN. This detector turns any future render loop
 * into a screenshotable console line BEFORE the tab dies: if the chat system
 * renders more times inside a one-second window than any legitimate animation
 * can produce, it fires a single structured `console.error` and goes silent.
 *
 * Threshold calibration: legitimate rapid renders (streaming token dispatches,
 * rAF-driven typewriter reveal, resize observers) are frame-capped at
 * ~60–120/s. A synchronous render loop (state update during render/effect
 * feedback) commits as fast as React can cycle — hundreds to thousands per
 * second. 240 renders in 1000ms sits 2–4× above the legitimate ceiling and
 * ~an order of magnitude below a real storm, so it discriminates cleanly.
 *
 * The tracker mutates state held in a ref and allocates nothing on the hot
 * path — it runs on EVERY render of the chat system and must never contribute
 * to the pressure it is measuring.
 */

export const RENDER_STORM_WINDOW_MS = 1_000;
export const RENDER_STORM_THRESHOLD = 240;

export interface RenderStormState {
  windowStartMs: number;
  count: number;
  fired: boolean;
}

export function createRenderStormState(): RenderStormState {
  return { windowStartMs: 0, count: 0, fired: false };
}

/**
 * Record one render at `nowMs`. Returns true exactly ONCE per page load: the
 * first time the render rate exceeds RENDER_STORM_THRESHOLD renders within
 * RENDER_STORM_WINDOW_MS. After firing it stops counting entirely (one-shot:
 * the console line must not itself become log spam inside a storm).
 */
export function trackRenderStorm(state: RenderStormState, nowMs: number): boolean {
  if (state.fired) return false;
  if (nowMs - state.windowStartMs > RENDER_STORM_WINDOW_MS) {
    state.windowStartMs = nowMs;
    state.count = 0;
  }
  state.count += 1;
  if (state.count > RENDER_STORM_THRESHOLD) {
    state.fired = true;
    return true;
  }
  return false;
}

export interface RenderStormDiagnostics {
  route: string;
  viewMode: string;
  threadCount: number;
  activeThreadId: string | null;
  messageCount: number;
  rendersInWindow: number;
  windowMs: number;
  usedJSHeapMB: number | null;
}

/**
 * Snapshot the context a crash investigator needs. Called only at fire time
 * (never on the hot path), so the allocations here are irrelevant.
 * `performance.memory` is Chrome-only; null elsewhere.
 */
export function buildRenderStormDiagnostics(args: {
  state: RenderStormState;
  route: string;
  viewMode: string;
  threadCount: number;
  activeThreadId: string | null;
  messageCount: number;
}): RenderStormDiagnostics {
  let usedJSHeapMB: number | null = null;
  try {
    const memory = (globalThis.performance as unknown as {
      memory?: { usedJSHeapSize?: number };
    })?.memory;
    if (memory && typeof memory.usedJSHeapSize === 'number') {
      usedJSHeapMB = Math.round(memory.usedJSHeapSize / (1024 * 1024));
    }
  } catch {
    usedJSHeapMB = null;
  }
  return {
    route: args.route,
    viewMode: args.viewMode,
    threadCount: args.threadCount,
    activeThreadId: args.activeThreadId,
    messageCount: args.messageCount,
    rendersInWindow: args.state.count,
    windowMs: RENDER_STORM_WINDOW_MS,
    usedJSHeapMB,
  };
}
